import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import EntityMember from "_shared/entity-members/entity-member";
import { useState } from "react";
import TeamLeaderHandover from "work/work-agreements/teams/leader-handover/team-leader-handover";
import useTeamIndividuals, { nameOf } from "work/work-agreements/teams/leader-handover/_views/use-team-individuals";

export type LeaderHandoverBannerProps = {
  handover: TeamLeaderHandover;
  /** Who is reading, as the organization they act for. */
  viewer: EntityMember | undefined;
  /** Accepting ends the work: the team is restricted, or the leader who leaves holds a rate. */
  endsTheWork?: boolean;
  busy?: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onWithdraw: () => void;
  /** Approving a vendor team handover during negotiation. Absent on hired work, which nobody approves. */
  onApprove?: (choice: { outgoingLeaderStays?: boolean; grantSigningAuthority?: boolean }) => void;
  /**
   * Whether the reader may grant signing authority (`User.canGrantSigningAuthority`). Only then is an
   * outgoing leader approving a handover asked to grant it to a successor who lacks it.
   */
  canGrantSigningAuthority?: boolean;
};

/**
 * A handover of a team's leadership waiting for an answer, as each person it concerns needs to read
 * it (#5836): the successor is asked to accept, the other side's leader to approve, whoever offered
 * it can take it back, and anybody else on the team is told what is coming.
 */
export default function LeaderHandoverBanner(props: Readonly<LeaderHandoverBannerProps>) {
  const { handover, viewer, busy, onAccept, onDecline, onWithdraw, onApprove } = props;
  const individuals = useTeamIndividuals([
    handover.outgoingLeader,
    handover.successor,
    handover.initiatedBy,
    handover.approver,
  ]);
  const [outgoingLeaderStays, setOutgoingLeaderStays] = useState(handover.outgoingLeaderStays);
  const [grantSigningAuthority, setGrantSigningAuthority] = useState(false);
  const outgoing = nameOf(individuals, handover.outgoingLeader);
  const successor = nameOf(individuals, handover.successor);
  const initiator = nameOf(individuals, handover.initiatedBy);
  const approver = nameOf(individuals, handover.approver);
  const team = handover.teamName;
  const proposedByTheOtherSide = !handover.initiatedBy.isEqualTo(handover.outgoingLeader);

  if (handover.awaitsAnswerFrom(viewer)) {
    return (
      <Alert
        severity="info"
        role="region"
        aria-label="Team leader handover"
        action={
          <Stack direction="row" spacing={1}>
            <Button color="inherit" disabled={busy} onClick={onDecline}>
              Decline
            </Button>
            <Button variant="contained" disabled={busy} onClick={onAccept}>
              Accept
            </Button>
          </Stack>
        }
      >
        <AlertTitle>You are asked to lead the {team}</AlertTitle>
        {proposedByTheOtherSide
          ? `${initiator} proposed that you take over from ${outgoing}.`
          : `${outgoing} asked you to take over the ${team}.`}
        {handover.awaitsApproval ? ` ${approver} must approve as well.` : ""}
        {props.endsTheWork
          ? " The handover changes a term of the agreement, so the work ends when you accept and a new proposal you lead opens to negotiate."
          : ""}
      </Alert>
    );
  }

  if (handover.awaitsApprovalFrom(viewer) && onApprove) {
    const approverLeavesTheTeam = handover.outgoingLeader.isEqualTo(viewer);
    // Unknown until the directory answers; the server decides then, and says why if it refuses.
    const successorLacksSigningAuthority =
      individuals.get(handover.successor.userId.value)?.hasSigningAuthority === false;
    const mayGrant = approverLeavesTheTeam && props.canGrantSigningAuthority === true;
    const nobodyGrantsIt = approverLeavesTheTeam && successorLacksSigningAuthority && !mayGrant;
    return (
      <Alert
        severity="info"
        role="region"
        aria-label="Team leader handover"
        action={
          <Stack direction="row" spacing={1}>
            <Button color="inherit" disabled={busy} onClick={onDecline}>
              Decline
            </Button>
            <Button
              variant="contained"
              disabled={busy || nobodyGrantsIt}
              onClick={() =>
                onApprove(
                  approverLeavesTheTeam
                    ? { outgoingLeaderStays, grantSigningAuthority: mayGrant && grantSigningAuthority }
                    : {},
                )
              }
            >
              Approve
            </Button>
          </Stack>
        }
      >
        <AlertTitle>Approve a new {team} leader</AlertTitle>
        {approverLeavesTheTeam
          ? `${initiator} proposed that ${successor} lead your ${team} in place of you.`
          : `${initiator} wants to hand the ${team} over to ${successor}.`}
        {approverLeavesTheTeam && (
          <Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={outgoingLeaderStays}
                  onChange={(event) => setOutgoingLeaderStays(event.target.checked)}
                />
              }
              label="Stay on the team as a member"
            />
            {mayGrant && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={grantSigningAuthority}
                    onChange={(event) => setGrantSigningAuthority(event.target.checked)}
                  />
                }
                label={`Grant ${successor} signing authority for our firm if they do not hold it`}
              />
            )}
            {nobodyGrantsIt &&
              ` ${successor} needs signing authority for your firm to lead the team. Ask whoever manages your firm's permissions to grant it, then approve.`}
          </Stack>
        )}
      </Alert>
    );
  }

  const waitingOn = [
    handover.awaitsSuccessor ? `${successor} to accept` : undefined,
    handover.awaitsApproval ? `${approver} to approve` : undefined,
  ]
    .filter((part) => part !== undefined)
    .join(" and ");

  if (handover.wasOfferedBy(viewer)) {
    return (
      <Alert
        severity="info"
        role="region"
        aria-label="Team leader handover"
        action={
          <Button color="inherit" disabled={busy} onClick={onWithdraw}>
            Withdraw
          </Button>
        }
      >
        <AlertTitle>Handing over the {team}</AlertTitle>
        You offered the {team} to {successor}. Waiting for {waitingOn}.
      </Alert>
    );
  }

  return (
    <Alert severity="info" role="region" aria-label="Team leader handover">
      <AlertTitle>A new {team} leader is on the way</AlertTitle>
      {successor} has been asked to take over the {team} from {outgoing}. Waiting for {waitingOn}.
    </Alert>
  );
}
